import { useEffect, useState } from 'react';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { cn } from '@/utils/classNames.ts';
import { ArrowDownIcon, ArrowUpIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  InitialTableState,
  PaginationState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';

type DataTableProps<T> = {
  rowClassName?: string;
  headClassName?: string;
  wrapperClassName?: string;
  zebra?: boolean;
  data: T[];
  columns: ColumnDef<T, any>[];
  filters?: ColumnFiltersState;
  initialState?: InitialTableState;
  showPagination?: boolean;
  // Server-side pagination props
  manualPagination?: boolean;
  pageCount?: number;
  rowCount?: number;
  onPaginationChange?: (updater: PaginationState | ((old: PaginationState) => PaginationState)) => void;
  paginationState?: PaginationState;
  // Loading state
  loading?: boolean;
};
function DataTable<T>({
  className,
  rowClassName,
  headClassName,
  wrapperClassName,
  zebra,
  data,
  columns,
  filters,
  initialState,
  showPagination,
  manualPagination = false,
  pageCount,
  rowCount,
  onPaginationChange: externalOnPaginationChange,
  paginationState: externalPaginationState,
  loading = false,
}: PropsWithClassName<DataTableProps<T>>) {
  const [sorting, setSorting] = useState<SortingState>(initialState?.sorting ?? []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialState?.columnFilters ?? filters ?? []);
  const [internalPagination, setInternalPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });

  const pagination = externalPaginationState ?? internalPagination;
  const setPagination = externalOnPaginationChange ?? setInternalPagination;

  const table = useReactTable({
    initialState,
    data,
    columns,
    pageCount: pageCount,
    rowCount: rowCount,
    manualPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: showPagination ? setPagination : undefined,
    getPaginationRowModel: showPagination && !manualPagination ? getPaginationRowModel() : undefined,
    state: {
      sorting,
      columnFilters,
      pagination: showPagination ? pagination : undefined,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  useEffect(() => {
    setColumnFilters(filters ?? []);
  }, [filters]);

  return (
    <div className={cn('overflow-x-auto w-full relative', wrapperClassName)}>
      {loading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
          <span className="loading loading-spinner loading-lg text-brand-active"></span>
        </div>
      )}
      <table className={cn('table table-pin-rows w-full bg-white', className)}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className={cn('border-base-300 border-b', headClassName)}>
              {headerGroup.headers.map((header) => (
                <th key={header.id}>
                  <div
                    onClick={() =>
                      header.column.getCanSort()
                        ? header.column.toggleSorting(
                            !(header.column.getIsSorted() === 'desc' || !header.column.getIsSorted())
                          )
                        : null
                    }
                    className={cn('flex items-center gap-2', {
                      'cursor-pointer select-none': header.column.getCanSort(),
                    })}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort()
                      ? ({
                          asc: <ArrowUpIcon className="w-3" />,
                          desc: <ArrowDownIcon className="w-3" />,
                          false: <ArrowDownIcon className="w-3" />,
                        }[header.column.getIsSorted() as string] ?? null)
                      : null}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, index) => (
            <tr
              key={row.id}
              className={cn(
                'border-base-300',
                {
                  'bg-base-300': zebra && index % 2 === 0,
                },
                rowClassName
              )}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
              ))}
            </tr>
          ))}
          {showPagination && !!table.getRowModel().rows.length && (
            <tr className={cn('border-base-300', rowClassName)}>
              <td colSpan={columns.length}>
                <div className="flex justify-end items-center gap-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Rows per page</span>
                    <select
                      value={pagination.pageSize}
                      onChange={(e) => {
                        table.setPageSize(Number(e.target.value));
                        table.setPageIndex(0);
                      }}
                      className="text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-700 cursor-pointer"
                    >
                      {[15, 50, 100, 150].map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                  <span className="text-sm text-gray-600">
                    {pagination.pageIndex * pagination.pageSize + 1}-
                    {Math.min((pagination.pageIndex + 1) * pagination.pageSize, table.getRowCount())} of{' '}
                    {table.getRowCount()}
                  </span>
                  <div className="flex gap-2">
                    <button
                      className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    <button
                      className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;
